
import { Container, Point } from 'pixi.js';
import { Chunk } from './chunk';
import { Map as map } from './map';
import { CollisionGraph } from './collisionGraph';
import { Collectible } from './Collectible';
import { dataImporter } from './../DataImporter';
import { app } from './../app';

export class Tilemap extends Container {
    /**
     * @param {map} map
     */
    constructor(map, viewport) {
        super();

        this.viewport = viewport;
        this.map = map;

        // Setup chunks.
        if (app.performanceManager.smallChunks){
            //INVERT THESE BEFORE DEPLOYMENT
            this.chunkPoolSize = 50;
        }
        else{
            this.chunkPoolSize = 100;
        }

        this.chunks = [];
        this.chunkMaxSize = 256;
        this.chunkMinSize = Math.min(Math.min(this.map.tileWidth, this.map.tileHeight), this.chunkMaxSize);
        this.calculateChunkSize(256, 256);
        this.setupChunkPool(this.chunkPoolSize, this.chunkWidth, this.chunkHeight);

        this.addedChunks = [];
        this.containerBelow = new Container();
        this.containerEntities = new Container();
        this.containerPlayer = new Container();
        this.containerCollectibles = new Container();
        this.containerObstacles = new Container();
        this.containerAbove = new Container();
        this.addChild(this.containerBelow, this.containerEntities, this.containerCollectibles, this.containerObstacles, this.containerPlayer, this.containerAbove);

        this.collisionGraph = new CollisionGraph(this);
        this.collectibles = new Map();
        this.spawnCollectibles();
        this.updated = false;
    }

    get width() {
        return this.map.width;
    }

    get height() {
        return this.map.height;
    }

    get tileWidth() {
        return this.map.tileWidth;
    }

    get tileHeight() {
        return this.map.tileHeight;
    }

    get chunkWidth() {
        return this._chunkWidth;
    }

    get chunkHeight() {
        return this._chunkHeight;
    }

    get worldWidth() {
        return this.map.width * this.map.tileWidth;
    }

    get worldHeight() {
        return this.map.height * this.map.tileHeight;
    }

    get tileOffset() {
        return new Point(this.map.tileWidth * 0.5, this.map.tileHeight * 0.5);
    }

    get spawnTile() {
        return this.map.data.spawnPos;
    }

    addEntity(entity) {
        this.collisionGraph.addStaticEntity(entity);
        this.containerEntities.addChild(entity);
    }

    removeEntity(entity) {
        this.collisionGraph.removeStaticEntity(entity);
        this.containerEntities.removeChild(entity);
    }

    addObstacle(obstacle) {
        this.containerObstacles.addChild(obstacle);
        this.collisionGraph.addObstacles(obstacle);
    }

    removeObstacle(obstacle) {
        this.collisionGraph.removeObstacles(obstacle);
        this.containerObstacles.removeChild(obstacle);
    }

    addPlayer(player) {
        this.containerPlayer.addChild(player);
    }

    removePlayer(player) {
        this.containerPlayer.removeChild(player);
    }

    // Chunk size must be wholly divisible by tile size.
    calculateChunkSize(suggestedWidth, suggestedHeight) {
        this._chunkWidth = 640;
        this._chunkHeight = 640;
        //this.chunkWidth = Math.max(Math.min(Math.floor(suggestedWidth / this.map.tileWidth), this.chunkMaxSize), this.chunkMinSize);
        //this.chunkHeight = Math.max(Math.min(Math.floor(suggestedHeight / this.map.tileHeight), this.chunkMaxSize), this.chunkMinSize);
    }

    setupChunkPool(poolSize, chunkWidth, chunkHeight) {
        for (let i = 0; i < poolSize; i++) {
            this.chunks.push(new Chunk(chunkWidth, chunkHeight, this.map));
        }
    }

    getFreeChunk() {
        let chunk = this.chunks.find((chunk) => {
            return !chunk.inUse && !chunk.hasTiles;
        });

        // If we can't find a free chunk, recycle one not in current use.
        if (!chunk) {
            chunk = this.chunks.find((chunk) => {
                return !chunk.inUse;
            });
        }
        chunk.inUse = true;
        return chunk;
    }

    getChunkFor(mapId, pos, layersToRender) {
        let chunk = this.chunks.find((chunk) => {
            return !chunk.inUse && chunk.mapId === mapId &&
                chunk.x === pos.x &&
                chunk.y === pos.y &&
                chunk.z === pos.z;
        });

        if (chunk) {
            chunk.inUse = true;
            return chunk;
        }

        chunk = this.getFreeChunk();
        chunk.position = pos;
        chunk.render(layersToRender);
        return chunk;
    }

    updateChunks(e) {
        let viewport = e.viewport;
        // let viewport = this.viewport;
        let bounds = viewport.getVisibleBounds();

        let minX = Math.floor(bounds.x / this.chunkWidth),
            maxX = Math.ceil((bounds.x + bounds.width) / this.chunkWidth),
            minY = Math.floor(bounds.y / this.chunkHeight),
            maxY = Math.ceil((bounds.y + bounds.height) / this.chunkHeight);
        // Add chunk margin.

        // Clear out past chunks.
        for (let i = 0; i < this.addedChunks.length; i++){
            this.addedChunks[i].inUse = false;
        }

        this.addedChunks = [];

        this.containerAbove.removeChildren();
        this.containerBelow.removeChildren();

        for (let i = minX; i <= maxX; i++){
            for (let j = minY; j <= maxY; j++) {
                let belowPos = { x: i, y: j, z: -1 };
                let abovePos = { x: i, y: j, z: 1 };
                let renderBelow = this.getChunkFor(this.map.id, belowPos, this.map.visualBottomLayers);
                let renderAbove = this.getChunkFor(this.map.id, abovePos, this.map.visualTopLayers);

                this.containerBelow.addChild(renderBelow.texture);
                this.containerAbove.addChild(renderAbove.texture);
                this.addedChunks.push(renderAbove, renderBelow);
            }
        }
        /*
        if (this.updated)
            return;

        for (let i = 0; i < 100; i++) {
            let chunkBelow = this.getFreeChunk();
            let chunkAbove = this.getFreeChunk();

            let x = i % 10;
            let y = Math.floor(i / 10);

            let below = chunkBelow.render({ x: x, y: y, z: -1 }, this.map.visualBottomLayers);
            let above = chunkAbove.render({ x: x, y: y, z: 1 }, this.map.visualTopLayers);
            this.containerBelow.addChild(below);
            this.containerAbove.addChild(above);
        }

        this.updated = true;
        */
    }

    spawnCollectibles() {
        let colData = this.map.collectionData.getAll();
        for (let i = 0; i < colData.length; i++) {
            let idx = this.indexToWorldXY(colData[i]);
            let collectible = new Collectible(this.map.tilesets[0].tiles[dataImporter.currPack.world.tiledIdToCollection]);
            collectible.x = idx.x;
            collectible.y = idx.y;
            this.containerCollectibles.addChild(collectible);
            this.collectibles.set(colData[i], collectible);
        }
    }

    indexToWorldXY(index) {
        return this.localToWorldXY(this.indexToXY(index)[0], this.indexToXY(index)[1]);
    }

    indexToXY(index) {
        let x = (index % this.width);
        let y = Math.floor(index / this.map.width);
        return [x, y];
    }

    localXYToIndex(x, y) {
        return x + y * this.map.width;
    }

    worldXYToIndex(x, y) {
        return this.localXYToIndex(Math.floor(x / this.map.tileWidth), Math.floor(y / this.map.tileHeight));
    }

    worldToLocalXY(x, y) {
        return { x: Math.floor(x / this.map.tileWidth), y: Math.floor(y / this.map.tileHeight) };
    }

    localToWorldXY(x, y) {
        return { x: x * this.map.tileWidth, y: y * this.map.tileHeight };
    }

    /* highestPowerOf2(n) {
        return (n & (~(n - 1)));
    } */

    update(dt) {
        //this.updateChunks();
        // this.calcChunks();
    }

    calcChunks(){
        let viewport = this.viewport;
        let bounds = viewport.getVisibleBounds();
        let minX = Math.floor(bounds.x / this.chunkWidth),
            maxX = Math.ceil((bounds.x + bounds.width) / this.chunkWidth),
            minY = Math.floor(bounds.y / this.chunkHeight),
            maxY = Math.ceil((bounds.y + bounds.height) / this.chunkHeight);
        console.log((maxX - minX) * (maxY - minY));
    }
}
